import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RegisterService } from '../../../features/auth/services/register.service';

@Component({
  selector: 'app-header-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule
  ],
  templateUrl: './header-home.component.html',
  styleUrl: './header-home.component.scss'
})
export class HeaderHomeComponent implements OnInit, OnDestroy {
  // @Input() userName: string = 'Mauricio';
  @Input() notificationCount: number = 0;

  // ViewChild para el input de búsqueda
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  // Propiedades de búsqueda
  isSearchExpanded: boolean = false;
  searchQuery: string = '';
  nombreUsuario!: string;
  serviceName: string | null = null;
  @Input() hideHeader = false;

  // Propiedad para controlar el menú móvil
  isMobileMenuOpen: boolean = false;

  // NUEVAS PROPIEDADES INTELIGENTES
  currentSection: string = 'home';
  showSearch: boolean = true;
  showNotifications: boolean = true;
  hasExtraHeight: boolean = false; // Para altura extra en beneficios, servicios y solicitudes
  isSolicitudesSection: boolean = false; // Para diferenciar solicitudes (61px) de otros (114px)
  customTitle: string = ''; // Título personalizado para beneficios, servicios y solicitudes
  showSearchBar: boolean = false; // Controla si se muestra la barra de búsqueda en el título personalizado

  // Para unsubscribe automático
  private destroy$ = new Subject<void>();

  // Eventos para comunicación con componentes padre
  @Output() searchClicked = new EventEmitter<void>();
  @Output() searchSubmitted = new EventEmitter<string>();
  @Output() notificationClicked = new EventEmitter<void>();
  @Output() menuClicked = new EventEmitter<void>();
  @Output() navigationClicked = new EventEmitter<string>();
  productName = '';

  constructor(private route: ActivatedRoute, private router: Router, private registerService: RegisterService) { }

  /**
   * INICIALIZACIÓN DEL HEADER INTELIGENTE
   */
  ngOnInit(): void {
    this.dataHeader();
    // Detectar cambios de ruta en tiempo real
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      this.updateHeaderState(event.url);
    });

    // Detectar página inicial
    this.updateHeaderState(this.router.url);

    //this.loadProducts();
  }

  dataHeader() {
    const userString = localStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      this.nombreUsuario = user.name;
    }
  }

  /**
   * Cleanup al destruir el componente
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * LÓGICA INTELIGENTE - Auto-detecta en qué sección estamos
   */
  private updateHeaderState(url: string): void {
    this.dataHeader();
    console.log('Detectando sección desde URL:', url);

    // Verificar si debemos ocultar el header (línea amarilla)
    const serviceIdMatch = url.match(/\/service\/\d+/);
    const solicitudDetalleMatch = url.match(/\/solicitud\/detalle\/\d+/);
    this.hideHeader = (url.includes('/service/') && url.includes('hideHeader=true')) || 
                      url.includes('/service/confirm/') || 
                      url.includes('solicitud/medical-checkup') ||
                      serviceIdMatch !== null ||
                      solicitudDetalleMatch !== null;

    if (url.includes('/services')) {
      this.currentSection = 'services';
      this.showSearch = false;
      this.showNotifications = true;
      this.hasExtraHeight = true;
      this.isSolicitudesSection = false; // Usar altura de 114px
      this.customTitle = 'Todos tus servicios';
      this.showSearchBar = true; // Mostrar barra de búsqueda para servicios
    } else if (url.includes('/beneficios')) {
      this.currentSection = 'beneficios';
      this.showSearch = false;
      this.showNotifications = true;
      this.hasExtraHeight = true;
      this.isSolicitudesSection = false; // Usar altura de 114px
      this.customTitle = 'Todos tus beneficios';
      this.showSearchBar = true; // Mostrar barra de búsqueda para beneficios
    } else if (url.includes('/solicitudes')) {
      this.currentSection = 'solicitudes';
      this.showSearch = false;
      this.showNotifications = true;
      this.hasExtraHeight = true;
      this.isSolicitudesSection = true; // Usar altura de 61px
      this.customTitle = 'Servicios solicitados';
      this.showSearchBar = false; // NO mostrar barra de búsqueda para solicitudes
    } else if (url.includes('/perfil')) {
      this.currentSection = 'perfil';
      this.showSearch = false;
      this.showNotifications = true;
      this.hasExtraHeight = false;
      this.isSolicitudesSection = false;
      this.customTitle = '';
      this.showSearchBar = false;
    } else {
      this.currentSection = 'home';
      this.showSearch = true;
      this.showNotifications = true;
      this.hasExtraHeight = false;
      this.isSolicitudesSection = false;
      this.customTitle = '';
      this.showSearchBar = false;
    }

    console.log('Estado esta siendo actualizado:', {
      section: this.currentSection,
      showSearch: this.showSearch,
      showNotifications: this.showNotifications,
      hasExtraHeight: this.hasExtraHeight,
      customTitle: this.customTitle
    });
  }

  /**
   * Verifica si un botón de navegación está activo
   */
  isNavItemActive(section: string): boolean {
    return this.currentSection === section;
  }

  /**
   * Navega a una ruta específica
   * @param route Ruta a la que navegar
   */
  navigateTo(route: string): void {
    this.navigationClicked.emit(route);
    this.router.navigate([route]);
  }

  /**
   * Maneja el clic en el botón de búsqueda
   */
  onSearchClick(): void {
    if (!this.isSearchExpanded) {
      // Expandir la búsqueda
      this.isSearchExpanded = true;
      // Enfocar el input después de que se expanda
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 200);
    } else if (this.searchQuery.trim()) {
      // Si hay texto, buscar
      this.onSearchSubmit();
    }
    this.searchClicked.emit();
  }

  /**
   * Maneja el envío de la búsqueda (Enter)
   */
  onSearchSubmit(): void {
    if (this.searchQuery.trim()) {
      this.searchSubmitted.emit(this.searchQuery.trim());
      this.performSearch(this.searchQuery.trim());
      // Opcional: colapsar después de buscar
      // this.onSearchClose();
    }
  }

  /**
   * Maneja los cambios en el campo de búsqueda
   */
  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    // Búsqueda en tiempo real
    if (this.searchQuery.length > 2) {
      this.performSearch(this.searchQuery.trim());
    } else if (this.searchQuery.length === 0) {
      this.performSearch('');
    }
  }

  /**
   * Maneja la pérdida de foco del input
   */
  onSearchBlur(): void {
    // Solo colapsar si no hay texto
    if (!this.searchQuery.trim()) {
      this.onSearchClose();
    }
  }

  /**
   * Cierra la búsqueda expandida
   */
  onSearchClose(): void {
    this.isSearchExpanded = false;
    this.searchQuery = '';
  }

  /**
   * Maneja el clic en el botón de notificaciones
   */
  onNotificationClick(): void {
    this.notificationClicked.emit();
    console.log('Notificaciones clickeadas');
  }

  /**
   * Maneja el clic en el menú hamburguesa (móvil)
   */
  onMenuClick(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.menuClicked.emit();
    console.log('Menú clickeado - Estado:', this.isMobileMenuOpen ? 'Abierto' : 'Cerrado');
  }

  /**
   * Cierra el menú móvil
   */
  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  /**
   * Navega desde el menú móvil y lo cierra
   */
  navigateFromMobile(route: string): void {
    this.navigateTo(route);
    this.closeMobileMenu();
  }

  /**
   * Abre la búsqueda desde el menú móvil
   */
  onSearchFromMobile(): void {
    this.closeMobileMenu();
    // Pequeño delay para que se cierre el menú primero
    setTimeout(() => {
      this.onSearchClick();
    }, 300);
  }

  /**
   * Abre las notificaciones desde el menú móvil
   */
  onNotificationFromMobile(): void {
    this.closeMobileMenu();
    // Pequeño delay para que se cierre el menú primero
    setTimeout(() => {
      this.onNotificationClick();
    }, 300);
  }

  logout() {
    localStorage.clear();
    this.router.navigate(['/login']);
  }

  isAccountBanamex(): boolean {
    let allowedAccounts = ['2228', '2665', '2559', '2907', '2988', '2997', '2998', '2999'];
    const account = localStorage.getItem('account');
    return account ? allowedAccounts.includes(account) : false;
  }

  /**
   * Ejecuta la búsqueda y navega a servicios si es necesario
   */
  private performSearch(query: string): void {
    // Emitir evento personalizado para que otros componentes escuchen
    const searchEvent = new CustomEvent('headerSearch', {
      detail: query
    });
    window.dispatchEvent(searchEvent);

    // Si no estamos en la página de servicios, navegar allí
    if (!this.router.url.includes('/services')) {
      this.router.navigate(['/services'], {
        queryParams: query ? { search: query } : {}
      });
    }
  }

  loadProducts():void{
    setTimeout(()=>{
      const product = JSON.parse(localStorage.getItem('ProductByAccount') || '{}');
      if(product && product?.name){
        this.productName = product ? `${product?.name}`: "";
      }else{
        this.loadProductByAccount();
      }

    }, 1000);
  }

  loadProductByAccount(): void {
    this.registerService.getProducts().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const account = localStorage.getItem('account') || '0';
          const productByAccount = response.data.find(
            product => product.product === account
          );
          if(productByAccount){
            this.productName = productByAccount ? `${productByAccount?.name}`: "";
            localStorage.setItem('ProductByAccount', JSON.stringify(productByAccount));
          }
        }
      },
      error: (err) => {
        console.error('Error loading products:', err);
      }
    });
  }
}
